import React, { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import MinorSector from 'components/MapViewAngle/MinorSector';
import { MapIcon, CloseIcon, Minimize, Maximize } from 'assets/icons';
import './style.scss';

const DESKTOP_BREAKPOINT = 1024;
// const PORTRAIT_WIDTH = 460;
// const LANDSCAPE_WIDTH = 680;
const HEADING_INDICATOR_DELAY = 500;

const FloorMaps = ({
  floorMaps,
  currentPano,
  onSelect,
  cameraParams,
  sceneRotation,
}) => {
  const { scenes } = useSelector((state) => state);
  const [isOpen, setIsOpen] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hoveredHotspot, setHoveredHotspot] = useState(null);
  const [isDesktop, setIsDesktop] = useState(
    () => window.innerWidth >= DESKTOP_BREAKPOINT
  );
  const [headingIndicatorShow, setHeadingIndicatorShow] = useState(false);

  const floorMap = floorMaps?.[0];

  // Update heading indicator when pano changes
  useEffect(() => {
    setHeadingIndicatorShow(false);
    const timer = setTimeout(
      () => setHeadingIndicatorShow(true),
      HEADING_INDICATOR_DELAY
    );
    return () => clearTimeout(timer);
  }, [currentPano]);

  // Track viewport size
  useEffect(() => {
    const handleResize = () =>
      setIsDesktop(window.innerWidth >= DESKTOP_BREAKPOINT);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate expanded width based on image orientation
  useEffect(() => {
    if (!isDesktop || !floorMap?.image?.url) return;

    const img = new Image();
    // img.onload = () => {
    //   const isPortrait = img.naturalHeight > img.naturalWidth;
    //   setExpandedWidth(isPortrait ? PORTRAIT_WIDTH : LANDSCAPE_WIDTH);
    // };
    img.src = floorMap.image.url;
  }, [floorMap?.image?.url, isDesktop]);

  const handleToggleMap = useCallback(() => {
    setIsOpen((prev) => {
      if (prev) setIsExpanded(false);
      return !prev;
    });
  }, []);

  const handleToggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const handleHotspotClick = useCallback(
    (hotspot) => {
      const targetScene = scenes.find((scene) => scene._id === hotspot.id);
      if (targetScene && onSelect) {
        onSelect(targetScene.groupId, targetScene._id);
        setHeadingIndicatorShow(false);
      }
    },
    [scenes, onSelect]
  );

  const handleHotspotMouseEnter = useCallback((hotspotId) => {
    setHoveredHotspot(hotspotId);
  }, []);

  const handleHotspotMouseLeave = useCallback(() => {
    setHoveredHotspot(null);
  }, []);

  if (!floorMap?.image) return null;

  return (
    <div className={`floor-maps-container ${isOpen ? 'open' : 'closed'}`}>
      {!isExpanded && (
        <button className="map-icon-button" onClick={handleToggleMap}>
          {isOpen ? <CloseIcon color="#fff" /> : <MapIcon color="#fff" />}
        </button>
      )}

      {isOpen && (
        <div className={`floor-maps-overlay ${isExpanded ? 'expanded' : ''}`}>
          <div className="floor-maps-header">
            <div className="floor-maps-controls">
              <button className="expand-btn" onClick={handleToggleExpand}>
                {isExpanded ? (
                  <Minimize color="#fff" />
                ) : (
                  <Maximize color="#fff" size={20} />
                )}
              </button>
            </div>
          </div>

          <div className="floor-maps-content">
            <div className="floor-plan-wrapper">
              <img
                src={floorMap.image.url}
                alt={floorMap.image.name || 'Floor Plan'}
                className="floor-plan-image"
              />

              {floorMap.objects?.map((hotspot, index) => {
                const isActive = currentPano?._id === hotspot.id;
                const isHovered = hoveredHotspot === hotspot.id;

                return (
                  <React.Fragment key={hotspot.id || index}>
                    <div
                      className={`floor-plan-hotspot ${
                        isActive ? 'active' : ''
                      } ${isHovered ? 'hovered' : ''}`}
                      style={{
                        left: `${hotspot.coordinates[0] * 100}%`,
                        top: `${hotspot.coordinates[1] * 100}%`,
                      }}
                      onClick={() => handleHotspotClick(hotspot)}
                      onMouseEnter={() => handleHotspotMouseEnter(hotspot.id)}
                      onMouseLeave={handleHotspotMouseLeave}
                    >
                      <div className="hotspot-dot" />
                      {isHovered && hotspot.title && (
                        <div className="hotspot-tooltip">{hotspot.title}</div>
                      )}
                    </div>

                    {isActive && (
                      <div
                        className={`circular-sector ${
                          headingIndicatorShow ? 'show' : ''
                        }`}
                        style={{
                          position: 'absolute',
                          left: `${hotspot.coordinates[0] * 100}%`,
                          top: `${hotspot.coordinates[1] * 100}%`,
                        }}
                      >
                        <MinorSector
                          cameraParams={cameraParams}
                          sceneRotation={sceneRotation}
                          currentPano={currentPano}
                          defaultRotation={hotspot.defaultRotation}
                        />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FloorMaps;
